// Create a line chart component
import React, { Component } from 'react';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { isMobileWidth } from '../../theme/layoutUtilities';
import d3 from '../../utils/d3-import';
import { comparisonColors } from '../../utils/colors';
import { displayShortDate } from '../../utils/Format/Format';
import { responsiveAxes } from '../../utils/chart-util';
import styles from './LineChart.css';

class LineChart extends Component {
    static propTypes = {
        /**
         * Data to pass to the chart
         */
        data: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                headers: PropTypes.arrayOf(PropTypes.string),
                data: PropTypes.instanceOf(Array)
            })
        ),
        /**
         * marginBottom
         */
        marginBottom: PropTypes.number,
        /**
         * marginLeft
         */
        marginLeft: PropTypes.number,
        /**
         * width
         */
        width: PropTypes.number,
        /**
         * height
         */
        height: PropTypes.number,
        /**
         * colors
         */
        colors: PropTypes.arrayOf(PropTypes.string),
        /**
         * Function to round the y-axis scale
         */
        roundYAxis: PropTypes.func,
        /**
         * Toggle the tooltip
         */
        toggleTooltip: PropTypes.func,
        /**
         * Update the tooltip data
         */
        updateTooltip: PropTypes.func,
        /**
         * Format for Y-axis labels. D3 format
         */
        formatYAxis: PropTypes.string,
        /**
         * Format for Y-axis labels less than a certain value
         */
        formatYAxisDec: PropTypes.string,
        /**
         * Format for X-axis labels. D3 format ['mobile', 'desktop']
         */
        formatXAxis: PropTypes.arrayOf(PropTypes.string)
    };

    static defaultProps = {
        data: [
            {
                label: '',
                headers: [],
                data: []
            }
        ],
        marginLeft: 65,
        marginBottom: 75,
        width: 800,
        height: 400,
        colors: comparisonColors,
        roundYAxis: (num) => num * 1.25,
        toggleTooltip: () => {},
        updateTooltip: () => {},
        formatYAxis: '$,.0f',
        formatYAxisDec: '$.2f',
        formatXAxis: ['%y', '%Y']
    };

    constructor(props) {
        super(props);
        const { data } = this.props;
        this.xAxisRef = React.createRef();
        this.yAxisRef = React.createRef();
        this.gridlines = React.createRef();
        this.tooltip = React.createRef();
        this.tipLine = React.createRef();
        if (data.length > 0) {
            data[0].headers.forEach(
                // eslint-disable-next-line
                (_, i) => (this[`tipCircle-${i}`] = React.createRef())
            );
        }

        this.state = {
            lines: [],
            xScale: {},
            yScale: {},
            lineGenerator: d3.line()
        };
    }

    componentDidUpdate() {
        const {
            formatYAxis,
            formatYAxisDec,
            formatXAxis,
            width,
            marginLeft,
            xTickInterval
        } = this.props;
        const { yScale } = this.state;
        const xAxis = d3.select(this.xAxisRef.current);
        const yAxis = d3.select(this.yAxisRef.current);
        const formatter =
            yScale.domain()[1] - yScale.domain()[0] < 2
                ? formatYAxisDec
                : formatYAxis;
        this.xAxis = d3
            .axisBottom()
            .scale(this.state.xScale)
            .ticks(xTickInterval)
            .tickFormat(
                d3.timeFormat(width < 400 ? formatXAxis[0] : formatXAxis[1])
            );
        this.yAxis = d3
            .axisLeft()
            .scale(this.state.yScale)
            .ticks(5)
            .tickFormat(d3.format(formatter));
        xAxis.call(this.xAxis);
        yAxis.call(this.yAxis);
        d3.select(this.gridlines.current).call(
            d3
                .axisLeft(this.state.yScale)
                .ticks(5)
                .tickSize(-(width - marginLeft))
                .tickFormat('')
        );
        // Responsive chart logic
        responsiveAxes(width, yAxis, xAxis);
    }

    onMouseMove = (e) => {
        const { xScale, yScale, lines } = this.state;
        const {
            data,
            height,
            marginBottom,
            updateTooltip,
            accountForDuplicateData
        } = this.props;
        const { target } = e;
        const dim = target.parentNode.parentNode.getBoundingClientRect();

        const x = e.clientX - dim.left;
        const y = e.clientY - dim.top;

        const label = xScale.invert(x);

        const bisectData = this.props.data.map((d) => new Date(d.label));

        const bisect = d3.bisect(bisectData, new Date(label));

        const d0 = bisectData[bisect - 1];
        const d1 = bisectData[bisect];

        const d = label - d0 > d1 - label ? d1 : d0;
        const shortLabel = displayShortDate(d);
        const tipData = (accountForDuplicateData
            ? data.filter((ele) => ele.label === shortLabel).pop()
            : data.find((ele) => ele.label === shortLabel)) || {
            label: '',
            headers: [''],
            data: []
        };

        lines.forEach((ele, i) => {
            d3.select(this[`tipCircle-${i}`].current).attr(
                'transform',
                `translate(${xScale(d)}, ${yScale(tipData.data[i])})`
            );
        });

        d3.select(this.tipLine.current)
            .attr('x1', xScale(d))
            .attr('x2', xScale(d))
            .attr('y1', 0)
            .attr('y2', height - marginBottom)
            .attr('stroke', '#003366');

        updateTooltip(xScale(d), y, { data: { ...tipData } });
    };

    onTouchStart = (e) => {
        const { target } = e;
        const { touches } = e;

        if (!touches.length) return;
        d3.select(this.tipLine.current).attr('display', 'block');
        d3.selectAll('.tip-circle').attr('display', 'block');

        const lastTouch = touches[touches.length - 1];

        const { xScale, yScale, lines } = this.state;
        const {
            data,
            height,
            marginBottom,
            updateTooltip,
            accountForDuplicateData
        } = this.props;
        const dim = target.parentNode.parentNode.getBoundingClientRect();

        const x = lastTouch.clientX - dim.left;
        const y = lastTouch.clientY - dim.top;

        const label = xScale.invert(x);

        const bisectData = this.props.data.map((d) => new Date(d.label));

        const bisect = d3.bisect(bisectData, new Date(label));

        const d0 = bisectData[bisect - 1];
        const d1 = bisectData[bisect];

        const d = label - d0 > d1 - label ? d1 : d0;
        const shortLabel = displayShortDate(d);
        const tipData = (accountForDuplicateData
            ? data.filter((ele) => ele.label === shortLabel).pop()
            : data.find((ele) => ele.label === shortLabel)) || {
            label: '',
            headers: [''],
            data: []
        };

        lines.forEach((ele, i) => {
            d3.select(this[`tipCircle-${i}`].current).attr(
                'transform',
                `translate(${xScale(d)}, ${yScale(tipData.data[i])})`
            );
        });

        d3.select(this.tipLine.current)
            .attr('x1', xScale(d))
            .attr('x2', xScale(d))
            .attr('y1', 0)
            .attr('y2', height - marginBottom)
            .attr('stroke', '#003366');

        updateTooltip(xScale(d), y, { data: { ...tipData } }, true);
    };

    onLeave = () => {
        const { toggleTooltip } = this.props;
        toggleTooltip(false);
        d3.select(this.tipLine.current).attr('display', 'none');
        d3.selectAll('.tip-circle').attr('display', 'none');
    };

    onMouseEnter = () => {
        const { toggleTooltip } = this.props;
        toggleTooltip(true);
        d3.select(this.tipLine.current).attr('display', 'block');
        d3.selectAll('.tip-circle').attr('display', 'block');
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            data,
            width,
            height,
            marginLeft,
            marginBottom,
            roundYAxis
        } = nextProps;
        const { lineGenerator } = prevState;

        const formattedData = data.map((d) => ({
            ...d,
            label: new Date(d.label)
        }));

        const xDomain = LineChart.calcXDomain(formattedData);
        const yDomain = LineChart.calcYDomain(formattedData);

        const xScale = LineChart.calcXScale(marginLeft, width, xDomain);
        const yScale = LineChart.calcYScale(
            height,
            marginBottom,
            yDomain,
            roundYAxis
        );

        lineGenerator.x((d) => xScale(d.label));

        /* eslint-disable */
        // TODO - prettier is conflicing with eslint, throws an error after running prettier or manually matching to eslint spec
        const lines =
            formattedData.length > 0
                ? formattedData[0].headers.map((d, i) => {
                      lineGenerator.y((dd) => yScale(dd.data[i]));
                      return lineGenerator(formattedData);
                  })
                : [];

        return { lines, xScale, yScale };
        /* eslint-enable */
    }

    static calcXScale = memoize((marginLeft, width, xDomain) =>
        d3
            .scaleTime()
            .range([marginLeft, width])
            .domain(xDomain)
    );

    static calcYScale = memoize((height, marginBottom, yDomain, roundYAxis) => {
        const [yMin, yMax] = yDomain;
        return d3
            .scaleLinear()
            .range([height - marginBottom, 0])
            .domain([roundYAxis(yMin), roundYAxis(yMax)]);
    });

    static calcXDomain = memoize((data) => d3.extent(data, (d) => d.label));

    static calcYDomain = memoize((data) => {
        const [yMin, yMax] = d3.extent(
            data.reduce((a, v) => a.concat(v.data), [])
        );
        return [Math.min(yMin, 0), yMax];
    });

    render() {
        const isMobile = isMobileWidth();
        const { colors, height, width, marginLeft, marginBottom } = this.props;
        const { lines } = this.state;

        const ix = {};

        if (isMobile) {
            ix.onTouchStart = this.onTouchStart;
        } else {
            ix.onMouseEnter = this.onMouseEnter;
            ix.onMouseMove = this.onMouseMove;
            ix.onMouseLeave = this.onLeave;
        }

        return (
            <svg width={width + 15} height={height}>
                <g
                    className="line-gridlines"
                    ref={this.gridlines}
                    transform={`translate(${marginLeft}, 0)`}
                />
                <g className="tt-container">
                    <rect
                        width={width - marginLeft}
                        height={height - marginBottom}
                        x={marginLeft}
                        y={0}
                        opacity="0"
                        {...ix}
                    />
                    <line className="tip-line" ref={this.tipLine} />
                </g>
                <g className="lines">
                    {lines.map((line, i) => (
                        // eslint-disable-next-line
                        <React.Fragment key={i}>
                            <path d={line} stroke={colors[i]} />
                            <circle
                                ref={this[`tipCircle-${i}`]}
                                display="none"
                                className={cn(`tip-circle-${i}`, 'tip-circle')}
                                r="5"
                            />
                        </React.Fragment>
                    ))}
                </g>
                <g>
                    <g
                        className="x-axis"
                        ref={this.xAxisRef}
                        transform={`translate(0, ${height - marginBottom})`}
                    />
                    <g
                        className="y-axis"
                        ref={this.yAxisRef}
                        transform={`translate(${marginLeft}, 0)`}
                    />
                </g>
                <style jsx>{styles}</style>
            </svg>
        );
    }
}

export default LineChart;
